import { useParams, useNavigate } from 'react-router-dom'
import { Scale, Pencil, History } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { EmptyState } from '@/components/common/EmptyState'
import { PermissionGate } from '@/components/common/PermissionGate'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { SectionLoader } from '@/components/common/SectionLoader'
import { useTaxRule } from '@/features/tax-rules/hooks/useTaxRules'
import type { TaxRule } from '@/features/tax-rules/types/tax-rule.types'
import { SupersededNotice } from './TaxRuleSuperseded'
import { isSuperseded } from '@/features/tax-rules/utils/taxRuleVersions'

const RULE_TYPE_VARIANT: Record<string, 'neutral' | 'info' | 'warning' | 'success'> = {
  flat_rate: 'info',
  percentage: 'info',
  fixed_amount: 'warning',
  progressive_brackets: 'success',
  salary_range: 'success',
}

const titleCase = (s: string): string => s.replace(/_/g, ' ')
const num = (n: number): string => n.toLocaleString()
const fmtDate = (d?: string): string => (d ? new Date(d).toLocaleDateString() : '—')

/** One label/value row in a definition list. */
function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  )
}

export function TaxRuleViewPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: rule, isLoading, isError } = useTaxRule(id ?? null)

  const shell = (body: React.ReactNode, title = 'Tax Rule'): React.ReactElement => (
    <PageShell
      breadcrumbItems={[{ label: 'Tax Rules', to: '/tax-rules' }, { label: title }]}
      icon={Scale}
      title={title}
      description="Read-only view of the tax rule configuration"
      className="gap-0"
      hideHeader
    >
      {body}
    </PageShell>
  )

  if (isLoading) {
    return shell(<SectionLoader variant="detail" label="Loading tax rule" />)
  }

  if (isError || !rule) {
    return shell(
      <EmptyState
        icon={Scale}
        title="Tax rule not found"
        description="It may have been removed, or you don't have access to it."
      />
    )
  }

  const r: TaxRule = rule
  const isBracketed = r.ruleType === 'progressive_brackets' || r.ruleType === 'salary_range'

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Tax Rules', to: '/tax-rules' }, { label: r.name }]}
      icon={Scale}
      title={r.name}
      description="Read-only view of the tax rule configuration"
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          actions={
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/tax-rules/${r._id}/history`)}
              >
                <History className="h-3.5 w-3.5" />
                <span className="ml-1.5">History</span>
              </Button>
              {!isSuperseded(r) && (
                <PermissionGate module="tax_rules" action="update">
                  <Button size="sm" onClick={() => navigate(`/tax-rules/${r._id}/edit`)}>
                    <Pencil className="h-3.5 w-3.5" />
                    <span className="ml-1.5">Edit</span>
                  </Button>
                </PermissionGate>
              )}
            </>
          }
        />
        {isSuperseded(r) && <SupersededNotice rule={r} />}
        {/* Overview */}
        <Card header={<h2 className="text-sm font-semibold">Overview</h2>}>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
            <Field label="Country">
              <span className="font-mono">{r.country}</span>
            </Field>
            <Field label="Type">
              <Badge variant={RULE_TYPE_VARIANT[r.ruleType] ?? 'neutral'}>
                {titleCase(r.ruleType)}
              </Badge>
            </Field>
            <Field label="Status">
              <Badge variant={r.isActive ? 'success' : 'neutral'}>
                {r.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </Field>
            <Field label="Income basis">{titleCase(r.incomeBasis ?? '—')}</Field>
            <Field label="Version">v{r.version}</Field>
            <Field label="Applies to">
              <div className="flex flex-wrap gap-1">
                {r.appliesTo.length
                  ? r.appliesTo.map((c) => (
                      <Badge key={c} variant="info">
                        {titleCase(c)}
                      </Badge>
                    ))
                  : '—'}
              </div>
            </Field>
            <Field label="Effective from">{fmtDate(r.effectiveFrom)}</Field>
            <Field label="Effective to">{fmtDate(r.effectiveTo)}</Field>
            {r.rate != null && <Field label="Rate">{r.rate.toFixed(2)}%</Field>}
            {r.fixedAmount != null && <Field label="Fixed amount">{num(r.fixedAmount)}</Field>}
            <Field label="Taxable ceiling">
              {r.taxableCeiling != null ? num(r.taxableCeiling) : 'None'}
            </Field>
            <Field label="Security pre-tax">
              <Badge variant={r.deductSecurityBeforeTax ? 'success' : 'neutral'}>
                {r.deductSecurityBeforeTax ? 'Deducted before tax' : 'Not deducted'}
              </Badge>
            </Field>
          </dl>
          {r.description && (
            <p className="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
              {r.description}
            </p>
          )}
        </Card>

        {/* Brackets */}
        {isBracketed && (
          <Card
            header={
              <h2 className="text-sm font-semibold">
                {r.ruleType === 'salary_range' ? 'Slabs' : 'Brackets'}
                <span className="ml-2 font-normal text-gray-500">({r.brackets.length})</span>
              </h2>
            }
            contentClassName="p-0"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-2.5">From</th>
                    <th className="px-4 py-2.5">To</th>
                    <th className="px-4 py-2.5 text-right">Rate</th>
                    <th className="px-4 py-2.5 text-right">Fixed</th>
                  </tr>
                </thead>
                <tbody>
                  {r.brackets.map((b, i) => (
                    <tr
                      key={i}
                      className="border-b border-gray-100 last:border-0 dark:border-gray-700"
                    >
                      <td className="px-4 py-2.5 tabular-nums">{num(b.min)}</td>
                      <td className="px-4 py-2.5 tabular-nums">
                        {b.max == null ? '∞' : num(b.max)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{b.rate.toFixed(2)}%</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {b.fixedComponent ? num(b.fixedComponent) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Exemptions */}
        {r.exemptions.length > 0 && (
          <Card header={<h2 className="text-sm font-semibold">Exemptions</h2>}>
            <ul className="space-y-2">
              {r.exemptions.map((e, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 dark:text-gray-300">
                    {e.description || titleCase(e.type)}
                  </span>
                  <span className="tabular-nums font-medium">{num(e.amount)}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </PageShell>
  )
}

TaxRuleViewPage.displayName = 'TaxRuleViewPage'
